import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'gatsby';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';

import { Secondary } from './Secondary';
import { NavNewMark, SECTION_ICONS } from './NavDropdownMenus';
import { generateNavLink, generateNavLabel } from '@src/utils/nav';

import Logo from '../../assets/images/logo-small.svg';
import CloseIcon from '../../assets/icons/close.svg';

const ROOT_PANEL_TITLE = 'Menu';

const itemKey = (item) => {
  if (item.__typename === 'DatoCmsDropdownSection') return `section:${item.title}`;
  if (item.linkType === 'page') return `page:${item.page?.slug}`;
  if (item.linkType === 'category') return `cat:${item.category?.map((c) => c.slug).join('/')}`;
  return `link:${item.label}`;
};

const buildEntriesFromItems = (items) =>
  items
    .map((item) => {
      if (item.__typename === 'DatoCmsDropdownSection') {
        return {
          kind: 'section',
          key: itemKey(item),
          label: item.title,
          color: item.color,
          icon: item.icon,
          items: buildEntriesFromItems(item.items),
        };
      }

      if (item.disabled) {
        return {
          kind: 'disabled',
          key: itemKey(item),
          label: generateNavLabel(item),
        };
      }

      if (item.subItems?.length > 0) {
        return {
          kind: 'drill',
          key: itemKey(item),
          label: item.label,
          nextPanel: {
            title: item.label,
            entries: buildEntriesFromItems(item.subItems),
          },
        };
      }

      const url = generateNavLink(item.linkType, item);
      if (!url) return null;

      return {
        kind: 'link',
        key: itemKey(item),
        label: item.label,
        url,
      };
    })
    .filter(Boolean);

const buildPanelForLink = (link) => {
  if (link.items?.length > 0) {
    return {
      title: link.label,
      entries: buildEntriesFromItems(link.items),
    };
  }
  if (link.subNavigation?.length > 0) {
    return {
      title: link.label,
      entries: link.subNavigation.map((page) => ({
        kind: 'link',
        key: `nav:${page.slug}`,
        label: page.title,
        url: `/${page.slug}`,
      })),
    };
  }
  return null;
};

const buildRootPanel = (mainNav, user) => {
  const entries = mainNav.link.map((link) => {
    const nextPanel = buildPanelForLink(link);
    if (nextPanel) {
      return {
        kind: 'drill',
        key: `top:${link.label}`,
        label: link.label,
        isNew: Boolean(link.new),
        nextPanel,
      };
    }
    return {
      kind: 'link',
      key: `top:${link.label}`,
      label: link.label,
      isNew: Boolean(link.new),
      url: link.externalLink ? link.externalLink : `/${link.slug === 'home' ? '' : link.slug}`,
      external: Boolean(link.externalLink),
    };
  });

  if (user?.email) {
    entries.push({
      kind: 'link',
      key: 'top:profile',
      label: 'Profile',
      url: '/profile',
    });
  }

  return { title: ROOT_PANEL_TITLE, entries };
};

const rowClassName = (entry, extra = '') =>
  `mobile-nav-row${extra}${entry.isNew ? ' is-new' : ''}`;

const PanelRow = ({ entry, onDrill, onLeafClick }) => {
  if (entry.kind === 'drill') {
    return (
      <button
        type="button"
        className={rowClassName(entry, ' is-drill')}
        onClick={() => onDrill(entry.nextPanel)}
        aria-label={entry.isNew ? `${entry.label} (New)` : undefined}
      >
        <span className="mobile-nav-row-label">
          {entry.label}
          <NavNewMark isNew={entry.isNew} />
        </span>
        <FaChevronRight size={14} className="mobile-nav-row-chevron" aria-hidden="true" />
      </button>
    );
  }

  if (entry.kind === 'disabled') {
    return (
      <span className={rowClassName(entry, ' is-disabled')} aria-disabled="true">
        <span className="mobile-nav-row-label">
          {entry.label}
          <NavNewMark isNew={entry.isNew} />
        </span>
      </span>
    );
  }

  if (entry.external) {
    return (
      <a
        href={entry.url}
        target="_blank"
        rel="noreferrer"
        className={rowClassName(entry)}
        onClick={onLeafClick}
        aria-label={entry.isNew ? `${entry.label} (New)` : undefined}
      >
        <span className="mobile-nav-row-label">
          {entry.label}
          <NavNewMark isNew={entry.isNew} />
        </span>
      </a>
    );
  }

  return (
    <Link
      to={entry.url}
      className={rowClassName(entry)}
      onClick={onLeafClick}
      aria-label={entry.isNew ? `${entry.label} (New)` : undefined}
    >
      <span className="mobile-nav-row-label">
        {entry.label}
        <NavNewMark isNew={entry.isNew} />
      </span>
    </Link>
  );
};

const SectionGroup = ({ section, onDrill, onLeafClick }) => {
  const SectionIcon = section.icon ? SECTION_ICONS[section.icon] : null;
  return (
    <li className="mobile-nav-section">
      <div
        className="mobile-nav-section-title"
        style={section.color ? { color: section.color } : undefined}
      >
        {SectionIcon && <SectionIcon className="mobile-nav-section-icon" aria-hidden="true" />}
        <span>{section.label}</span>
      </div>
      <ul className="mobile-nav-section-list">
        {section.items.map((entry) => (
          <li key={entry.key}>
            <PanelRow entry={entry} onDrill={onDrill} onLeafClick={onLeafClick} />
          </li>
        ))}
      </ul>
    </li>
  );
};

const Panel = ({ panel, onDrill, onLeafClick }) => (
  <div className="mobile-nav-panel">
    <ul className="mobile-nav-list">
      {panel.entries.map((entry) => {
        if (entry.kind === 'section') {
          return (
            <SectionGroup
              key={entry.key}
              section={entry}
              onDrill={onDrill}
              onLeafClick={onLeafClick}
            />
          );
        }
        return (
          <li key={entry.key}>
            <PanelRow entry={entry} onDrill={onDrill} onLeafClick={onLeafClick} />
          </li>
        );
      })}
    </ul>
  </div>
);

const MobileNav = ({ mainNav, secondaryNav, user, isOpen, onClose }) => {
  const rootPanel = useMemo(() => buildRootPanel(mainNav, user), [mainNav, user?.email]);

  const [stack, setStack] = useState([rootPanel]);
  const [depth, setDepth] = useState(0);
  const railRef = useRef(null);
  const pendingPopRef = useRef(false);

  useEffect(() => {
    setStack([rootPanel]);
    setDepth(0);
  }, [rootPanel, isOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const drillTo = useCallback((nextPanel) => {
    setStack((prev) => [...prev, nextPanel]);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setDepth((d) => d + 1);
      });
    });
  }, []);

  const goBack = useCallback(() => {
    if (depth === 0) return;
    pendingPopRef.current = true;
    setDepth((d) => d - 1);
  }, [depth]);

  const handleRailTransitionEnd = useCallback(
    (e) => {
      if (e.target !== railRef.current) return;
      if (e.propertyName !== 'transform') return;
      if (pendingPopRef.current) {
        pendingPopRef.current = false;
        setStack((prev) => (prev.length > depth + 1 ? prev.slice(0, depth + 1) : prev));
      }
    },
    [depth]
  );

  const handleLeafClick = useCallback(() => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    onClose();
  }, [onClose]);

  const currentPanel = stack[depth] ?? rootPanel;
  const showBack = depth > 0;

  return (
    <nav
      role="navigation"
      className="mobile-nav w-nav-menu"
      style={{ display: isOpen ? 'flex' : 'none' }}
    >
      <div className="mobile-nav-header">
        <button
          type="button"
          className={`mobile-nav-back${showBack ? '' : ' is-hidden'}`}
          onClick={goBack}
          aria-label="Back"
          tabIndex={showBack ? 0 : -1}
        >
          <FaChevronLeft size={16} aria-hidden="true" />
        </button>
        {depth === 0 ? (
          <Link to="/" className="mobile-nav-header-brand" onClick={handleLeafClick}>
            <img src={Logo} alt="Logo" />
          </Link>
        ) : (
          <span className="mobile-nav-header-title">{currentPanel.title}</span>
        )}
        <button
          type="button"
          className="mobile-nav-close"
          onClick={onClose}
          aria-label="Close menu"
        >
          <img src={CloseIcon} alt="" />
        </button>
      </div>

      <div className="mobile-nav-viewport">
        <div
          ref={railRef}
          className="mobile-nav-rail"
          style={{ transform: `translateX(-${depth * 100}%)` }}
          onTransitionEnd={handleRailTransitionEnd}
        >
          {stack.map((panel, i) => (
            <Panel
              key={`${i}:${panel.title}`}
              panel={panel}
              onDrill={drillTo}
              onLeafClick={handleLeafClick}
            />
          ))}
        </div>
      </div>

      {secondaryNav?.link && (
        <div className="mobile-nav-footer">
          <Secondary links={secondaryNav.link} />
        </div>
      )}
    </nav>
  );
};

export { MobileNav };
